<template>
  <div>
    <div class="mb-3">
      <div class="font-weight-medium">Where this migration is</div>
      <div>{{ texts.where }}</div>
    </div>

    <div class="mb-3">
      <div class="font-weight-medium">What rolling back means now</div>
      <div>{{ texts.means }}</div>
    </div>

    <div v-if="steps.length" class="mb-3">
      <div class="font-weight-medium mb-1">Steps</div>
      <v-simple-table dense>
        <thead>
          <tr><th>#</th><th>Step</th><th>System</th><th>Time</th><th>State</th></tr>
        </thead>
        <tbody>
          <tr v-for="(s, i) in steps" :key="`step-${s.key}`">
            <td>{{ i + 1 }}</td>
            <td>{{ s.label }}</td>
            <td>{{ s.system }}</td>
            <td>{{ s.duration }}</td>
            <td :class="stateClass(s)">{{ s.state }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <div class="mb-3">
      <div class="font-weight-medium">What customers experience</div>
      <div>{{ texts.customers }}</div>
    </div>

    <div v-if="outside.length" class="mb-3">
      <div class="font-weight-medium mb-1">Outside the tool</div>
      <div v-for="o in outside" :key="`outside-${o.key}`" class="mb-2">
        <div class="font-weight-medium">{{ o.title }}</div>
        <div class="caption">{{ o.text }}</div>
        <v-btn x-small text color="primary" class="ml-2" @click="copy(o)">Copy</v-btn>
        <span v-if="copyNotes[o.key]" class="caption">{{ copyNotes[o.key] }}</span>
      </div>
    </div>

    <v-alert v-if="notPossible.length" type="info" dense outlined class="mb-3">
      <div class="font-weight-medium">Not possible</div>
      <div v-for="(line, i) in notPossible" :key="`not-possible-${i}`">{{ line }}</div>
    </v-alert>

    <div v-if="actions.length" class="mb-3">
      <step-button
        v-for="a in actions"
        :key="`action-${a.key}`"
        :override="a"
        :label="a.label"
        :color="a.color || 'primary'"
        :outlined="!!a.outlined"
        :busy="busy"
        :loading="loadingKey === a.key"
        @click="onAction(a)"
      />
    </div>

    <cvc-migration-rollback-after
      v-if="showAfter"
      :ctx="afterCtx"
      :preflight="preflight"
      :cvc-id="cvcId"
      :now="now"
      :busy="busy"
      :kick-run="kickRun"
      :kick-loop="kickLoop"
      :kick-busy="kickBusy"
      :kick-note="kickNote"
      @busy="$emit('busy', $event)"
      @changed="$emit('changed')"
      @close-rolled-back="closeRolledBack"
      @source-verified="sourceVerify = $event"
    />

    <v-alert v-if="outcome" :type="outcome.type" dense outlined class="mb-3">
      {{ outcome.text }}
      <div v-if="outcome.notRestored.length" class="mt-2">
        <div class="font-weight-medium">Not restored ({{ outcome.notRestored.length }}): these statics may be on the wrong IP and work slowly until fixed</div>
        <ul class="mb-1">
          <li v-for="(r, i) in outcome.notRestored" :key="`not-restored-${i}`">
            <strong>{{ r.avcId }}</strong> service {{ r.radiusServiceId }}: {{ r.change }}
          </li>
        </ul>
        <step-button
          :override="retryRestore"
          :label="`Retry restore (${outcome.notRestored.length})`"
          color="error"
          :busy="busy"
          :loading="loadingKey === 'restore'"
          @click="restoreOpen = true"
        />
      </div>
      <v-expansion-panels v-if="outcome.rows.length" accordion flat class="mt-1">
        <v-expansion-panel>
          <v-expansion-panel-header class="px-0">Rows ({{ outcome.rows.length }})</v-expansion-panel-header>
          <v-expansion-panel-content class="px-0">
            <ul class="mb-0">
              <li v-for="(r, i) in outcome.rows" :key="`outcome-row-${i}`" :class="toneClass(r.tone)">
                <strong>{{ r.avcId }}</strong> {{ r.outcome }}: {{ r.message }}
              </li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-alert>

    <v-alert v-if="cancelResult" :type="cancelResult.failed ? 'error' : 'success'" dense outlined class="mb-3">
      {{ cancelResult.text }}
      <step-button
        v-if="retry"
        :override="retry"
        :label="retry.label"
        :busy="busy"
        :loading="loadingKey === 'release'"
        class="ml-2"
        @click="retryRelease"
      />
    </v-alert>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mb-3">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <div class="mb-3">
      <div class="font-weight-medium mb-1">Results</div>
      <div v-if="!status.shut && !status.rollback && !restoreRows.length" class="grey--text text--darken-1">Nothing has run yet.</div>
      <div v-if="status.shut" :class="toneClass(status.shut.tone)">Shut: {{ status.shut.text }}</div>
      <div v-if="status.rollback" :class="toneClass(status.rollback.tone)">Rollback: {{ status.rollback.text }}</div>
      <v-simple-table v-if="restoreRows.length" dense class="mt-2">
        <thead>
          <tr><th>AVC</th><th>User</th><th>New -> old</th><th>Outcome</th></tr>
        </thead>
        <tbody>
          <tr v-for="(r, i) in restoreRows" :key="`restore-${i}`">
            <td>{{ r.avcId }}</td>
            <td>{{ r.user }}</td>
            <td>{{ r.change }}</td>
            <td :class="toneClass(r.tone)">{{ r.outcome }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <div class="mb-1">
      <div class="font-weight-medium mb-1">History</div>
      <div v-if="!history.length" class="grey--text text--darken-1">No rollback action yet.</div>
      <v-simple-table v-else dense>
        <thead>
          <tr><th>Time (SYD . local)</th><th>Who</th><th>What</th><th>Result</th></tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in history" :key="`history-${i}`">
            <td>{{ row.timeText }}</td>
            <td>{{ row.who }}</td>
            <td>{{ row.what }}</td>
            <td>{{ row.result }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />
    <type-to-confirm v-model="rollbackOpen" v-bind="rollbackDlg" :cvc-name="preflight.cvcName" @proceed="rollback" />
    <type-to-confirm v-model="restoreOpen" v-bind="restoreDlg" :cvc-name="preflight.cvcName" @proceed="restore" />
    <type-to-confirm v-model="unshutOpen" v-bind="unshutDlg" :cvc-name="preflight.cvcName" @proceed="unshutOnly" />
    <type-to-confirm v-model="cancelOpen" v-bind="cancelProps" :cvc-name="preflight.cvcName" @proceed="cancelMigration" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  cancelDialog,
  cutoverStatus,
  dialogProps,
  planCancel,
  releaseRetry,
  runCancelWizard,
  runReleaseRetry,
  stepButton,
} from "@/utils/cvcMigrationPage";
import {
  AFTER_POINTS,
  afterActions,
  afterContext,
  afterDialog,
  afterNotPossible,
  afterOutside,
  afterSteps,
  afterTexts,
} from "@/utils/cvcMigrationRollbackAfter";
import {
  restoreResultRows,
  rollbackActions,
  rollbackAnswerView,
  rollbackContext,
  rollbackDialog,
  rollbackHistory,
  rollbackNotPossible,
  rollbackOutside,
  rollbackPoint,
  rollbackRefusalRows,
  rollbackSteps,
  rollbackTexts,
} from "@/utils/cvcMigrationRollback";
import StepButton from "./step-button";
import TypeToConfirm from "./type-to-confirm";
import SectionActions from "./section-actions";
import CvcMigrationRollbackAfter from "./cvc-migration-rollback-after";

const TONE_CLASS = {
  green: "green--text text--darken-2",
  amber: "orange--text text--darken-3",
  red: "red--text text--darken-2",
  grey: "grey--text text--darken-2",
};

// Panel R: the one place that offers an undo, and the right one for the point the
// migration has reached. Everything shown is read from the Preflight answer and
// the last reconnection answer, so a reload or a second operator sees the same.
// The buttons are on only when the server says so; the dialogs ask for a tick or
// the typed CVC name by what the step changes.
export default {
  name: "CvcMigrationRollback",
  components: { StepButton, TypeToConfirm, CvcMigrationRollbackAfter },
  mixins: [SectionActions],
  props: {
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    targetHostId: { type: Number, required: true },
    now: { type: Date, required: true },
    reconnect: { type: Object, default: null },
    // The page's kick run, for the run of restored statics.
    kickRun: { type: Object, default: null },
    kickLoop: { type: Boolean, default: false },
    kickBusy: { type: Boolean, default: false },
    kickNote: { type: String, default: "" },
  },
  data: () => ({
    copyNotes: {},
    rollbackOpen: false,
    restoreOpen: false,
    unshutOpen: false,
    cancelOpen: false,
    cancelResult: null,
    outcome: null,
    // This session's last "Verify source": the steps and the network lines read it.
    sourceVerify: null,
  }),
  computed: {
    point () {
      return rollbackPoint(this.preflight, this.reconnect);
    },
    ctx () {
      return rollbackContext(this.preflight, this.reconnect, this.now);
    },
    // A rollback after the re-point confirm (or the record it closed) has its own rules.
    isAfter () {
      return AFTER_POINTS.includes(this.point);
    },
    // The after panel owns the buttons; a closed record only has leftovers, kept here.
    showAfter () {
      return this.isAfter && this.point !== "closedRolledBack";
    },
    afterCtx () {
      return afterContext(this.preflight, this.reconnect, this.sourceVerify, this.now);
    },
    texts () {
      return this.isAfter ? afterTexts(this.point, this.afterCtx) : rollbackTexts(this.point, this.ctx);
    },
    steps () {
      return this.isAfter ? afterSteps(this.afterCtx) : rollbackSteps(this.point, this.ctx);
    },
    outside () {
      return this.isAfter ? afterOutside(this.afterCtx) : rollbackOutside(this.point, this.ctx);
    },
    notPossible () {
      return this.isAfter ? afterNotPossible(this.point, this.afterCtx) : rollbackNotPossible(this.point, this.ctx);
    },
    actions () {
      if (this.point === "closedRolledBack") return afterActions(this.preflight.steps, this.afterCtx);
      if (this.isAfter) return [];
      return rollbackActions(this.point, this.preflight.steps, this.ctx).filter(a => !(a.key === "restore" && this.retryShown));
    },
    // After the rollback without the restore: the list of statics and its own retry.
    retryShown () {
      return !!this.outcome && this.outcome.notRestored.length > 0;
    },
    // The normal restore, open once RADIUS can be read.
    retryRestore () {
      return stepButton(this.preflight.steps, "canRestore");
    },
    status () {
      return cutoverStatus(this.preflight.record, this.now, this.preflight.poiState);
    },
    history () {
      return rollbackHistory(this.preflight.record, this.preflight.poiState, this.now, this.ctx.ledger);
    },
    restoreRows () {
      return restoreResultRows(this.preflight);
    },
    // D2 when nothing is on a new IP, D3 when the old IPs go back first.
    rollbackKind () {
      return this.ctx.c > 0 ? "restoreAndBringUp" : "bringUp";
    },
    rollbackDlg () {
      return dialogProps(rollbackDialog(this.rollbackKind, this.preflight, this.reconnect));
    },
    restoreDlg () {
      return dialogProps(rollbackDialog("restore", this.preflight, this.reconnect));
    },
    unshutDlg () {
      return dialogProps(rollbackDialog("unshutOnly", this.preflight, this.reconnect));
    },
    cancelInfo () {
      return cancelDialog(this.preflight);
    },
    cancelProps () {
      return dialogProps(this.cancelInfo);
    },
    retry () {
      return releaseRetry(this.cancelResult, this.preflight.steps);
    },
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationRollbackSource",
      "cvcMigrationRestoreOldIps",
      "cvcMigrationCancel",
      "cvcMigrationCloseRolledBack",
      "cvcMigrationRemoveTargetCopies",
      "cvcMigrationReleasePreAssignments",
    ]),
    toneClass (tone) {
      return TONE_CLASS[tone] || TONE_CLASS.grey;
    },
    // A step of the after rules carries its tone; the older steps are read from their state.
    stateClass (step) {
      if (step.tone !== undefined) return TONE_CLASS[step.tone] || "";
      const state = step.state;
      if (/^Failed/.test(state)) return TONE_CLASS.red;
      if (/^Done/.test(state)) return TONE_CLASS.green;
      if (state === "Running") return TONE_CLASS.amber;
      return "";
    },
    async copy (item) {
      let note = "Copied";
      try {
        await navigator.clipboard.writeText(item.text);
      } catch (e) {
        note = "Could not copy - select the text above";
      }
      this.copyNotes = { ...this.copyNotes, [item.key]: note };
    },
    onAction (a) {
      if (a.key === "cancel") this.cancelOpen = true;
      else if (a.key === "rollback") this.rollbackOpen = true;
      else if (a.key === "restore") this.restoreOpen = true;
      else if (a.key === "unshutOnly") this.unshutOpen = true;
      else if (a.key === "retryShut") this.$emit("open-cutover");
      else if (a.key === "removeTarget") this.askRemoveTarget();
      else if (a.key === "retryRelease") this.retryRelease();
    },
    // The restore runs first and the un-shut is queued in the same request (the
    // server enforces it); the answer lists every static, failures first.
    rollback () {
      this.run("rollback", () => this.answered(() => this.cvcMigrationRollbackSource({ cvcId: this.cvcId })));
    },
    restore () {
      this.run("restore", () => this.answered(() => this.cvcMigrationRestoreOldIps({ cvcId: this.cvcId })));
    },
    // Only while RADIUS cannot be read: the un-shut is queued, no IP is restored,
    // and the answer lists the statics that were left.
    unshutOnly () {
      this.run("unshutOnly", () => this.answered(() =>
        this.cvcMigrationRollbackSource({ cvcId: this.cvcId, unshutWithoutRestore: true })));
    },
    // A refusal that follows a restore still shows what the restore did.
    async answered (call) {
      this.outcome = null;
      try {
        const answer = await call();
        this.outcome = rollbackAnswerView(answer);
      } catch (e) {
        const rows = rollbackRefusalRows(e);
        if (rows.length) this.outcome = { type: "warning", text: "Restore results", rows, notRestored: [] };
        throw e;
      }
    },
    // Close the record, remove the target copies, release the reserved IPs: it
    // stops at the first step that fails and names it.
    cancelMigration ({ checked }) {
      const info = this.cancelInfo;
      const plan = planCancel({ targetCopyIds: info.targetCopyIds, reserved: info.reserved, release: checked });
      this.cancelResult = null;
      this.run("cancel", async () => {
        this.cancelResult = await runCancelWizard({
          plan,
          runStep: step => {
            if (step.key === "cancel") return this.cvcMigrationCancel({ cvcId: this.cvcId });
            if (step.key === "remove") {
              return this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: step.copyIds });
            }
            return this.cvcMigrationReleasePreAssignments({ cvcId: this.cvcId });
          },
        });
      });
    },
    // Close the record as rolled back, remove the target copies, release the new IPs: the
    // cancel wizard with its own words. It stops at the first step that fails and names it.
    closeRolledBack ({ checked }) {
      const info = afterDialog("close", this.afterCtx);
      const plan = planCancel({ targetCopyIds: info.targetCopyIds, reserved: info.reserved, release: checked });
      this.cancelResult = null;
      this.run("close", async () => {
        this.cancelResult = await runCancelWizard({
          plan,
          stopped: "Close stopped",
          done: "Closed as rolled back",
          runStep: step => {
            if (step.key === "cancel") return this.cvcMigrationCloseRolledBack({ cvcId: this.cvcId });
            if (step.key === "remove") {
              return this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: step.copyIds });
            }
            return this.cvcMigrationReleasePreAssignments({ cvcId: this.cvcId });
          },
        });
      });
    },
    // The record is closed by then, so only the release is run again.
    retryRelease () {
      this.run("release", async () => {
        this.cancelResult = await runReleaseRetry({
          runStep: () => this.cvcMigrationReleasePreAssignments({ cvcId: this.cvcId }),
        });
      });
    },
    // Leftover target copies of a cancelled migration.
    askRemoveTarget () {
      const ids = this.cancelInfo.targetCopyIds;
      this.ask(`Remove ${ids.length} target copies from ${this.preflight.targetHostname}?`, "Remove", () =>
        this.run("removeTarget", () => this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: ids })));
    },
  },
};
</script>
